export enum AdminTabs {
  Admins = 'admins',
  UserSearch = 'user-search',
  UserVerifications = 'user-verifications',
  LocationVerifications = 'location-verifications',
  ReportedLocations = 'reported-locations',
  ReportedUsers = 'reported-users',
  PastDueInvoices = 'past-due-invoices',
  FiscalDocuments = 'fiscal-documents',
  PendingFacturas = 'pending-facturas',
  ClosedDeals = 'closed-deals',
  Ownership = 'ownership',
  DirectoryClaims = 'directory-claims'
}

/**
 * Admin tabs cluster into three semantic groups. The header surfaces these
 * as a top-level filter so mobile users aren't scrolling a 10-tab strip and
 * desktop users see related work next to each other. The active group is
 * derived from the active tab — never set independently.
 */
export enum AdminTabGroup {
  Operations = 'operations',
  Finance = 'finance',
  Directory = 'directory'
}

export const ADMIN_TAB_GROUP: Record<AdminTabs, AdminTabGroup> = {
  [AdminTabs.Admins]: AdminTabGroup.Operations,
  [AdminTabs.UserVerifications]: AdminTabGroup.Operations,
  [AdminTabs.LocationVerifications]: AdminTabGroup.Operations,
  [AdminTabs.ReportedLocations]: AdminTabGroup.Operations,
  [AdminTabs.ReportedUsers]: AdminTabGroup.Operations,
  [AdminTabs.Ownership]: AdminTabGroup.Operations,
  [AdminTabs.DirectoryClaims]: AdminTabGroup.Operations,

  [AdminTabs.PastDueInvoices]: AdminTabGroup.Finance,
  [AdminTabs.ClosedDeals]: AdminTabGroup.Finance,
  [AdminTabs.FiscalDocuments]: AdminTabGroup.Finance,
  [AdminTabs.PendingFacturas]: AdminTabGroup.Finance,

  [AdminTabs.UserSearch]: AdminTabGroup.Directory
}

/**
 * Dashboard-vocabulary classes for the admin panel — the same tokens as the
 * shell (`Layouts/DashboardLayout/shellTokens.ts`), `screens/dashboard/today/*`
 * and `Teams/teamUi.tsx` (docs/claude/design-language.md → "Dashboard
 * vocabulary"). Change them here, not inline.
 *
 * Status is NEVER coloured text — use `<StatusPill tone label />`
 * (`screens/dashboard/today/StatusPill.tsx`). The notice washes below keep
 * their copy `text-ink-700`: the palette has no AA-legible red/orange text.
 */
export const ADMIN_UI = {
  /** White surface on the cream canvas: hairline, no shadow. */
  card: 'rounded-xl border border-ink-100/15 bg-white-500',
  /** Sentence-case card title row — no tinted header strip. */
  cardHeader: 'flex items-center justify-between gap-3 px-4 pt-3.5 pb-2',
  cardTitle: 'text-[14px] font-semibold text-ink-700',
  /** Key/value table rows inside a card. */
  kvRow: 'border-t border-ink-100/15',
  kvLabel:
    'w-28 px-4 py-2 align-top text-[12px] break-words text-ink-200 md:w-40',
  kvValue: 'px-4 py-2 text-[13px] break-words text-ink-700',
  /** Detail-pane page heading (h2 — the admin shell owns the h1). */
  detailTitle:
    'text-[18px] leading-tight font-semibold text-ink-700 md:text-[20px]',
  detailMeta: 'text-[12px] text-ink-200',
  focusRing:
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800',
  /** Rows sit flush in a panel, so their ring goes inside the row. */
  rowFocus:
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-800',
  textLink:
    'text-[13px] font-medium text-blue-800 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800',
  secondaryButton:
    'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full border border-ink-100/15 bg-white-500 px-4 text-[13px] font-medium text-ink-700 hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:opacity-60',
  /** Neutral tag (role, type, field name) — not a status, never a status tint. */
  chip: 'inline-flex shrink-0 items-center rounded-full bg-cream-300 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-ink-700',
  /** Quick-pick chip button (e.g. rejection presets). */
  chipButton:
    'rounded-full border border-ink-100/15 bg-white-500 px-3 py-1 text-left text-[12px] text-ink-700 hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800',
  emptyState:
    'flex items-center justify-center px-4 py-12 text-center text-[13px] text-ink-200',
  /** Master list column of a list | detail queue. */
  listPanel: 'bg-white-500',
  /** Same easing as the shell sidebar collapse (`SHELL.transition`). */
  panelTransition:
    'motion-safe:transition-[width] motion-safe:duration-150 motion-safe:ease-out',
  /** Queue as the whole page (nothing selected): the dashboard page column. */
  pageColumn: 'mx-auto w-full max-w-[1120px] px-4 py-4 md:px-8 md:py-6',
  /** Queue row: flush, hairline-divided, designed hover + selected states. */
  listRow:
    'relative flex w-full flex-col gap-1 border-t border-ink-100/15 px-4 py-3 text-left first:border-t-0',
  listRowIdle: 'hover:bg-cream-300',
  listRowSelected: 'bg-blue-500/10',
  /** The 2px brand rail on the selected row (same as the shell nav). */
  listRowRail: 'absolute top-2 bottom-2 left-0 w-[2px] rounded-r bg-blue-500',
  /** Hover tooltip (desktop affordance only). */
  tooltip:
    'pointer-events-none absolute bottom-full mb-1.5 hidden rounded-md bg-ink-800 px-2 py-1 text-[11px] font-medium whitespace-nowrap text-white-500 group-hover:block',
  notice: {
    success: 'rounded-xl border border-green-300 bg-green-100 text-ink-700',
    warning: 'rounded-xl border border-orange-300 bg-orange-100 text-ink-700',
    error: 'rounded-xl border border-red-300 bg-red-100 text-ink-700',
    info: 'rounded-xl border border-blue-500/20 bg-blue-500/5 text-ink-700',
    neutral: 'rounded-xl border border-ink-100/15 bg-cream-300 text-ink-700'
  },
  /** Skeleton bones — the Listings/Deals skeleton shades. */
  bone: 'rounded bg-cream-300',
  boneSoft: 'rounded bg-cream-300/60'
} as const
